import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Eye, Star, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminArticles = () => {
  const { getAuthHeaders } = useAuth();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, article: null });

  useEffect(() => {
    fetchData();
  }, [searchQuery, selectedCategory]);

  const fetchData = async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (selectedCategory) params.category = selectedCategory;

      const [articlesRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/articles`, { params }),
        axios.get(`${API_URL}/api/categories`)
      ]);
      setArticles(articlesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load articles');
    }
    setLoading(false);
  };

  const handleTogglePublish = async (article) => {
    try {
      await axios.post(`${API_URL}/api/articles/${article.id}/toggle-publish`, {}, getAuthHeaders());
      toast.success(article.is_published ? 'Article unpublished' : 'Article published');
      fetchData();
    } catch (error) {
      toast.error('Failed to update article');
    }
  };

  const handleSetFeatured = async (article) => {
    try {
      await axios.post(`${API_URL}/api/articles/${article.id}/set-featured`, {}, getAuthHeaders());
      toast.success('Featured article updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to set featured article');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.article) return;
    try {
      await axios.delete(`${API_URL}/api/articles/${deleteDialog.article.id}`, getAuthHeaders());
      toast.success('Article deleted');
      setDeleteDialog({ open: false, article: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl text-[#2C2C2C]" data-testid="articles-title">Articles</h1>
            <p className="font-body text-[#767C91]">{articles.length} total articles</p>
          </div>
          <Link to="/admin/articles/new">
            <Button className="bg-[#9D6E6D] hover:bg-[#2C2C2C] text-white rounded-none" data-testid="new-article-btn">
              <Plus className="w-4 h-4 mr-2" /> New Article
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#767C91]" />
            <Input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-none border-[#E2E8F0]"
              data-testid="articles-search"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-[#E2E8F0] bg-white text-sm"
            data-testid="articles-category-filter"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.slug}>{cat.name}</option>
            ))}
          </select>
        </div>

        {/* Articles Table */}
        <div className="bg-white border border-[#E2E8F0] overflow-hidden">
          {loading ? (
            <div className="p-8 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 loading-skeleton"></div>
              ))}
            </div>
          ) : articles.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="articles-table">
                <thead className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
                  <tr>
                    <th className="text-left px-6 py-4 font-label text-xs text-[#767C91] tracking-widest">ARTICLE</th>
                    <th className="text-left px-6 py-4 font-label text-xs text-[#767C91] tracking-widest">CATEGORY</th>
                    <th className="text-left px-6 py-4 font-label text-xs text-[#767C91] tracking-widest">STATUS</th>
                    <th className="text-left px-6 py-4 font-label text-xs text-[#767C91] tracking-widest">DATE</th>
                    <th className="text-right px-6 py-4 font-label text-xs text-[#767C91] tracking-widest">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.map((article) => (
                    <tr key={article.id} className="border-b border-[#E2E8F0] hover:bg-[#F8F9FA]">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <img 
                            src={article.hero_image} 
                            alt="" 
                            className="w-16 h-12 object-cover"
                          />
                          <div className="min-w-0">
                            <h3 className="font-heading text-sm text-[#2C2C2C] line-clamp-1">{article.title}</h3>
                            <p className="font-body text-xs text-[#767C91]">/{article.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-label text-xs text-[#767C91]">{article.category}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs font-label ${article.is_published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {article.is_published ? 'Published' : 'Draft'}
                          </span>
                          {article.is_featured && (
                            <Star className="w-4 h-4 text-[#9D6E6D] fill-current" />
                          )}
                          {article.is_pillar_guide && (
                            <span className="px-2 py-1 text-xs font-label bg-[#F2E1E1] text-[#9D6E6D]">Pillar</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-body text-sm text-[#767C91]">{formatDate(article.date_published)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleTogglePublish(article)}
                            className="p-2 text-[#767C91] hover:text-[#9D6E6D] hover:bg-[#F2E1E1]"
                            title={article.is_published ? 'Unpublish' : 'Publish'}
                          >
                            {article.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleSetFeatured(article)}
                            className={`p-2 hover:bg-[#F2E1E1] ${article.is_featured ? 'text-[#9D6E6D]' : 'text-[#767C91] hover:text-[#9D6E6D]'}`}
                            title="Set as Featured"
                          >
                            <Star className={`w-4 h-4 ${article.is_featured ? 'fill-current' : ''}`} />
                          </button>
                          <Link
                            to={`/admin/articles/edit/${article.id}`}
                            className="p-2 text-[#767C91] hover:text-[#9D6E6D] hover:bg-[#F2E1E1]"
                          >
                            <Edit className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => setDeleteDialog({ open: true, article })}
                            className="p-2 text-[#767C91] hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <p className="font-body text-[#767C91] mb-4">No articles found</p>
              <Link to="/admin/articles/new">
                <Button className="bg-[#9D6E6D] hover:bg-[#2C2C2C] text-white rounded-none">
                  Create Your First Article
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, article: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Article</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDialog.article?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminArticles;
